import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IMatchScheduleItem } from '../../../../../types/schedule';
import { UnscheduledMatches } from './UnscheduledMatches';
import { ScheduleConflictsPanel } from './ScheduleConflictsPanel';
import type { SchedulePlacementController } from './schedulePlacementState';
import type { ScheduleConflict } from '../../../../../features/schedule/scheduleConflictDetector';
import './ScheduleLeftRail.scss';

export type ScheduleLeftRailTab = 'unscheduled' | 'conflicts';

type ScheduleLeftRailProps = {
  activeTab: ScheduleLeftRailTab;
  onActiveTabChange: (tab: ScheduleLeftRailTab) => void;
  unscheduledCount: number;
  conflictCount: number;
  unscheduledMatches: IMatchScheduleItem[];
  placement: SchedulePlacementController;
  isMobile: boolean;
  scheduleMutationBlocked: boolean;
  selectedDayKey: number;
  onSelectedDayKeyChange: (dayKey: number) => void;
  onFocusConflictOnGrid: (conflict: ScheduleConflict) => void;
  onViewConflictMatch: (matchId: number) => void;
  showRail: boolean;
};

export const ScheduleLeftRail = memo(function ScheduleLeftRail({
  activeTab,
  onActiveTabChange,
  unscheduledCount,
  conflictCount,
  unscheduledMatches,
  placement,
  isMobile,
  scheduleMutationBlocked,
  selectedDayKey,
  onSelectedDayKeyChange,
  onFocusConflictOnGrid,
  onViewConflictMatch,
  showRail,
}: ScheduleLeftRailProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  if (!showRail) {
    return null;
  }

  const railClassName = [
    'schedule-workbench__left-rail',
    isMobile ? 'schedule-workbench__left-rail--mobile-drawer' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={railClassName}
      style={{ minHeight: 0 }}
      data-schedule-place-anchor="true"
    >
      <div className="schedule-left-rail__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'unscheduled'}
          className={`schedule-left-rail__tab btn btn-sm ${
            activeTab === 'unscheduled' ? 'btn-primary' : 'btn-outline-secondary'
          }`}
          onClick={() => onActiveTabChange('unscheduled')}
        >
          {t(prefix + 'scheduleLeftRailUnscheduledTab', { count: unscheduledCount })}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'conflicts'}
          className={`schedule-left-rail__tab btn btn-sm ${
            activeTab === 'conflicts' ? 'btn-primary' : 'btn-outline-secondary'
          }`}
          onClick={() => onActiveTabChange('conflicts')}
        >
          {t(prefix + 'scheduleConflictsTab', { count: conflictCount })}
        </button>
      </div>

      <div className="schedule-left-rail__body" role="tabpanel">
        {activeTab === 'unscheduled' ? (
          unscheduledCount > 0 ? (
            <UnscheduledMatches
              pendingPlaceMatchId={placement.pendingPlaceMatchId}
              pendingPlacementSource={placement.pendingPlacementSource}
              onSelectPendingMatch={placement.handleSelectUnscheduledForPlacement}
              onClearPendingFromDrag={() => placement.setClickPlacement(null)}
              isMobile={isMobile}
              scheduleMutationBlocked={scheduleMutationBlocked}
              dragModifierBlocked={placement.keyboardModifierHeld}
            />
          ) : (
            <p className="small text-muted mb-0 px-1">{t(prefix + 'scheduleUnscheduledEmpty')}</p>
          )
        ) : (
          <ScheduleConflictsPanel
            selectedDayKey={selectedDayKey}
            onSelectedDayKeyChange={onSelectedDayKeyChange}
            onFocusConflictOnGrid={onFocusConflictOnGrid}
            onViewConflictMatch={onViewConflictMatch}
          />
        )}
      </div>
    </aside>
  );
});
